import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'

/** Empty → relative ``/api`` URLs (dev server proxies when configured). */
export const PATH_URL = (import.meta.env.VITE_API_BASE_PATH ?? '').trim()

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'true' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  (error: AxiosError) => {
    const url = error.config?.url || ''
    abortControllerMap.delete(url)

    // Handle 401 UNAUTHORIZED errors (token expired or invalid)
    if (error.response?.status === 401) {
      const errorData = error.response?.data as any
      const errorMsg = errorData?.msg || errorData?.message || 'Token has expired'

      // Check if it's a token expiration error
      if (
        errorMsg.includes('Token has expired') ||
        errorMsg.includes('token') ||
        errorMsg.includes('expired') ||
        errorMsg.includes('unauthorized')
      ) {
        const userStore = useUserStoreWithOut()
        ElMessage.error('登录已过期，请重新登录')
        // Force logout and redirect to login page
        userStore.logout()
        return Promise.reject(error)
      }
    }

    // Handle other errors
    const errorMessage =
      (error.response?.data as any)?.msg || (error.response?.data as any)?.message || error.message
    ElMessage.error(errorMessage)
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
